// src/contexts/FavoritesContext.tsx
// Favoris sans compte utilisateur (cahier des charges §34), persistés en
// localStorage sur l'appareil du visiteur.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "secretdeal:favorites";

type FavoriteKind = "profile" | "product";
type FavoriteKey = `${FavoriteKind}:${string}`;

interface FavoritesContextValue {
  favorites: FavoriteKey[];
  isFavorite: (kind: FavoriteKind, id: string) => boolean;
  toggleFavorite: (kind: FavoriteKind, id: string) => void;
  count: number;
}

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

function readStoredFavorites(): FavoriteKey[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<FavoriteKey[]>(() => readStoredFavorites());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch {
      // stockage indisponible (navigation privée, quota atteint...) : on ignore silencieusement
    }
  }, [favorites]);

  const isFavorite = useCallback(
    (kind: FavoriteKind, id: string) => favorites.includes(`${kind}:${id}`),
    [favorites]
  );

  const toggleFavorite = useCallback((kind: FavoriteKind, id: string) => {
    const key: FavoriteKey = `${kind}:${id}`;
    setFavorites((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  const value = useMemo(
    () => ({ favorites, isFavorite, toggleFavorite, count: favorites.length }),
    [favorites, isFavorite, toggleFavorite]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavorites = (): FavoritesContextValue => {
  const context = useContext(FavoritesContext);
  if (!context) throw new Error("useFavorites must be used within a FavoritesProvider");
  return context;
};
